import { useState, type ReactNode } from "react";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { Button } from "@design-lab/shared/ui";
import { Tooltip, TooltipTrigger, TooltipContent } from "@design-lab/shared/ui/tooltip";
import { X } from "lucide-react";
import { cn } from "@design-lab/shared/lib/tailwindcss";

export type PrintPerfectExplanatoryTooltipProps = {
  setWidth?: string;
  children: ReactNode;
};

const DEFAULT_TOOLTIP_WIDTH = "580px";
const TOOLTIP_DELAY = 300;

export function PrintPerfectExplanatoryTooltip({
  setWidth,
  children
}: PrintPerfectExplanatoryTooltipProps) {
  const { isDesktop } = useResponsive();
  const [isHintOpen, setIsHintOpen] = useState(false);

  const handleClose = () => setIsHintOpen(false);
  const handleOpen = () => setIsHintOpen(true);

  // Desktop: Use regular tooltip
  if (isDesktop) {
    return (
      <Tooltip open={isHintOpen} delayDuration={TOOLTIP_DELAY} onOpenChange={setIsHintOpen}>
        <TooltipTrigger asChild data-testid="expert-edit-tooltip" onClick={handleOpen}>
          <Button
            data-testid="expert-edit-tooltip-button"
            variant="link"
            className="pr-1 text-blue-700 underline hover:bg-transparent hover:text-blue-700"
            aria-label="Expert Edit information"
          >
            Learn More
          </Button>
        </TooltipTrigger>
        <TooltipContent
          className="text-primary bg-background justify-center rounded-lg border border-gray-200 p-4 text-sm shadow-lg"
          style={{ width: setWidth || DEFAULT_TOOLTIP_WIDTH }}
          showArrow={false}
          side="right"
          align="center"
          alignOffset={-10}
        >
          {children}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Mobile: Use modal-like bottom overlay
  return (
    <>
      <Button
        data-testid="expert-edit-tooltip-button"
        variant="link"
        className="pr-1 text-xs text-blue-700 underline hover:bg-transparent hover:text-blue-700"
        aria-label="Expert Edit information"
        onPress={handleOpen}
      >
        Learn More
      </Button>

      {isHintOpen && (
        <div className="z-31 fixed inset-0 flex items-end pb-5">
          <div className="bg-foreground/60 absolute inset-0" onClick={handleClose} />
          <div
            className={cn(
              "bg-background relative w-full rounded-lg p-4",
              "animate-in slide-in-from-bottom-4 duration-300",
              "max-h-[95dvh] overflow-y-auto pb-[env(safe-area-inset-bottom)]"
            )}
          >
            <Button
              variant="icon"
              onPress={handleClose}
              aria-label="Close"
              className="absolute right-3 top-3 p-0"
              data-testid="expert-edit-tooltip-close-button"
            >
              <X className="scale-140" />
            </Button>

            <div className="text-primary text-sm">{children}</div>
          </div>
        </div>
      )}
    </>
  );
}
